import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';
import tokens from '@verifime/design-tokens';
import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

export type ZoomableImageFrameProps = {
  srcdoc: string;
};

/**
 * Renders the sandboxed image iframe with a parent-driven zoom + pan layer.
 *
 * Zoom resizes the iframe element's box (width/height %), so the browser
 * re-rasterises the inner full-resolution <img> at the larger layout size
 * rather than upscaling pixels it has already painted. We deliberately do NOT
 * use CSS transform: scale(), which rescales already-rendered pixels and
 * blurs fine text. Pan is click-drag on the overflow container; the iframe has
 * pointer-events: none so gestures reach the container (and the sandboxed
 * content stays non-interactive). The security attributes (sandbox="", CSP in
 * the srcdoc) are identical to the non-zoom path.
 */
export default function ZoomableImageFrame({ srcdoc }: Readonly<ZoomableImageFrameProps>) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const containerRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM);
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  }, []);

  // Reset zoom and scroll when the underlying file changes.
  useEffect(() => {
    resetView();
  }, [srcdoc, resetView]);

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100));
  const fit = resetView;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= MIN_ZOOM || !containerRef.current) return;
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      left: containerRef.current.scrollLeft,
      top: containerRef.current.scrollTop,
    };
    containerRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current || !containerRef.current) return;
    containerRef.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
    containerRef.current.scrollTop = drag.current.top - (e.clientY - drag.current.y);
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    if (containerRef.current?.hasPointerCapture(e.pointerId)) {
      containerRef.current.releasePointerCapture(e.pointerId);
    }
  };

  const zoomed = zoom > MIN_ZOOM;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <Box
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          position: 'relative',
          cursor: zoomed ? 'grab' : 'default',
          '&:active': { cursor: zoomed ? 'grabbing' : 'default' },
        }}
      >
        <iframe
          sandbox=""
          srcDoc={srcdoc}
          title="Sandboxed file viewer"
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            display: 'block',
            border: 'none',
            pointerEvents: 'none',
          }}
        />
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        gap={tokens.spacing2xs}
        sx={{
          px: tokens.spacingBase,
          py: tokens.spacing2xs,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          aria-label="Zoom out"
          data-action="zoom-out"
          size="small"
          onClick={zoomOut}
          disabled={zoom <= MIN_ZOOM}
        >
          <ZoomOutRoundedIcon fontSize="small" />
        </IconButton>
        {/* minWidth reserves space so the strip doesn't reflow as the readout grows (100% -> 150% ...). */}
        <Typography variant="body2" aria-live="polite" sx={{ minWidth: 44, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <IconButton
          aria-label="Zoom in"
          data-action="zoom-in"
          size="small"
          onClick={zoomIn}
          disabled={zoom >= MAX_ZOOM}
        >
          <ZoomInRoundedIcon fontSize="small" />
        </IconButton>
        <Button aria-label="Fit to screen" data-action="fit" size="small" onClick={fit} disabled={!zoomed}>
          Fit
        </Button>
      </Stack>
    </Box>
  );
}
